import { Injectable } from '@angular/core';
import { User } from './user.model';
import { UserService } from './user.service';

/*
Role: Support <profiles> in regards to social media links
Hold font-awesome links
Hold list of valid social media options (for profiles)
Make edit revertible, confirmable
*/
/* Procedure: add a new social media website to profiles
1. Find the social media icon in font awesome 5 (use their webs)
2. add the classes to iconDefinition
3. modify color to match brand
4. key names (ex: twitter) should be lowercase
*/

interface SocialMeta {
  bio:string,
  twitter:string,
  facebook:string,
  instagram:string,
  pinterest:string,
}
@Injectable({
  providedIn: 'root'
})
export class SocialService {

    //  actual instagram #f2c706, #d21c4d, #d12771
    icons = {
        twitter: { iconDefinition: "fab fa-twitter", color: "#059aee"},
        facebook: { iconDefinition: "fab fa-facebook", color: "#1773ea"},
        instagram: { iconDefinition: "fab fa-instagram", color: "#d12771"},
        pinterest: { iconDefinition: "fab fa-pinterest", color: "#059aee"},
    }

    defaultSocialMeta:SocialMeta = {
        bio: '',
        twitter: '',
        facebook: '',
        instagram: '',
        pinterest: '',
        /* someNewSite: '' */
    }

    beforeEditValues = {} //  hold pre-edit values to cancel changes

    constructor(private userService:UserService) {
    }

    /**
     * Get an icon from this.icons using a key
     * @param key the name of the key
     */
    getIcon(key: string): Object {
        return this.icons[key] || null
    }

    /**
     * Retrieve the default social meta values (minimum required for app)
     */
    getDefaultSocialMeta() {
        return this.defaultSocialMeta
    }

    /**
     * Set profile pre-edit values so that they can be restored
     * @param values the object containing the values to be stored
     */
    setPreEditValues(values: Object): void {
        this.beforeEditValues = {...values}
    }

    /**
     * Get the pre-edit values so the object can be restored
     * @returns the pre-edited values
     */
     getPreEditValues(): Object {
        return this.beforeEditValues
    }

    /**
     * Commit the edit values so that they are persistent
     * @param values the values to write into the meta property
     * @param user the user to change the 'meta' property of
     */
    commitEditValues(values: Object, user: User): void {
        //  set values onto the User object and save valid keys for api call
        const keyValues = {}
        const keys = Object.keys(values)
                            .filter((aKey) => {
                                if (values[aKey]) return true
                                // check if meta existed before, but is now being deleted (set to "")
                                if (aKey in this.beforeEditValues) return true
                            })
        keys.forEach((key) => {
            //  modify user for changes persistent across pages (re-add swarm_ prefix to keys)
            user.social[this.addPrefix(key)] = values[key]
            //  for api (server persistent)
            keyValues[key] = values[key]
        })
        //  send api request
        this.userService.updateUserMeta(keyValues, user, true);
    }

    /**
     * Remove the swarm_ prefix from a key
     * @param val a key in an object
     * @returns a string with the key name modified
     */
    stripPrefix(val: string): string { //swarm user_meta prefix
        const prefix = "swarm_"
        return val.replace('swarm_', '')
    }

    /**
     * Add the swarm_ prefix back to a key (this is used in User objects)
     * @returns the prefixed key value
     */
    addPrefix(val: string): string { //swarm user_meta prefix
        const prefix = "swarm_"
        return `${prefix}${val}`
    }

    /**
     * Get the keys from an object and run stripPrefix on them
     * @param val an object to gather keys from
     * @returns a new, unprefixed object
     */
    stripPrefixObject(val: Object): Object { //prefixes mess with input labels
        const unprefixedObj = {}
        const keys = Object.keys(val)
        keys.forEach(key => {
            unprefixedObj[key.replace('swarm_', '')] = val[key]
        })
        return unprefixedObj
    }

    /**
     * Add the swarm_ key prefix back to an object
     * @param obj the object to get the keys from
     * @returns a new object with modified key names
     */
    addPrefixObject(obj: Object): Object {
        const newObj = {}
        const prefix = "swarm_"
        const keys = Object.keys(obj)
        keys.forEach(i => newObj[prefix + i] = obj[i])
        return newObj
    }

    log(msg: any){ console.log(msg); }
    error(msg:any){ console.error(msg); }
    warn(msg:any){ console.warn(msg); }
}
